import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, Upload, Music2, Info, Loader2, Play, Pause, Check, Search, Coins, Trash2, Link as LinkIcon, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const STEPS = [
  { id: 1, label: "Konum", icon: MapPin },
  { id: 2, label: "Firma & Logo", icon: Upload },
  { id: 3, label: "Müzik", icon: Music2 },
  { id: 4, label: "Detaylar", icon: Info },
];

const GENRE_ORDER = ["Epik & Sinematik", "Sakin & Premium", "Kurumsal & Enerjik", "Lüks & Zarif"];

// TKGM koordinatlarını (Polygon/MultiPolygon) gezerek orta noktayı bul
function centroidOf(coordinates) {
  try {
    const pts = [];
    const walk = (c) => {
      if (typeof c[0] === "number") { pts.push(c); return; }
      c.forEach(walk);
    };
    walk(coordinates);
    if (!pts.length) return null;
    const sum = pts.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
    return { longitude: sum[0] / pts.length, latitude: sum[1] / pts.length };
  } catch {
    return null;
  }
}

export default function VideoWizard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [music, setMusic] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [audio] = useState(() => new Audio());
  const [parselData, setParselData] = useState(null);
  const [parselLoading, setParselLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [musicUploading, setMusicUploading] = useState(false);

  const [form, setForm] = useState({
    il: "", ilce: "", mahalle: "", ada: "", parsel: "",
    company_name: "", logo_data: null,
    music_id: "",
    distance_to_road: "", distance_to_sea: "", zoning_info: "", notes: "",
  });

  useEffect(() => {
    api.get("/music").then(({ data }) => setMusic(data));
    return () => { audio.pause(); };
  }, [audio]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onDrop = (files) => {
    const file = files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo 2MB'dan küçük olmalı");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => update("logo_data", e.target.result);
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp", ".svg"] }, maxFiles: 1,
  });

  const uploadUserMusic = async (files) => {
    const file = files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Müzik dosyası 8MB'dan büyük olamaz");
      return;
    }
    setMusicUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/music/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMusic(prev => [{ ...data, type: "user" }, ...prev]);
      update("music_id", data.music_id);
      toast.success("Müziğiniz yüklendi ve seçildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setMusicUploading(false);
    }
  };

  const musicDrop = useDropzone({
    onDrop: uploadUserMusic,
    accept: { "audio/*": [".mp3", ".wav", ".m4a", ".ogg", ".aac"] },
    maxFiles: 1,
  });

  const deleteUserMusic = async (m) => {
    if (!confirm(`"${m.name}" adlı müziği silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/music/${m.music_id}`);
      setMusic(prev => prev.filter(x => x.music_id !== m.music_id));
      if (form.music_id === m.music_id) update("music_id", "");
      toast.success("Müzik silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız");
    }
  };

  const toggleCatalog = async (m) => {
    const next = !(m.enabled !== false);
    try {
      await api.post("/music/catalog/toggle", { music_id: m.music_id, enabled: next });
      setMusic(prev => prev.map(x => x.music_id === m.music_id ? { ...x, enabled: next } : x));
      if (!next && form.music_id === m.music_id) update("music_id", "");
      toast.success(next ? `"${m.name}" kataloğa eklendi` : `"${m.name}" katalogdan kaldırıldı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const copyShareLink = async () => {
    if (!result?.share_slug) return;
    const url = `${window.location.origin}/v/${result.share_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Paylaşım linki kopyalandı");
    } catch {
      toast.error("Link kopyalanamadı");
    }
  };

  const togglePlay = (m) => {
    if (playing === m.music_id) {
      audio.pause(); setPlaying(null);
    } else {
      let src = m.url?.startsWith("http") ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`;
      // User music endpoint requires auth. <audio> can't send Bearer header — use ?auth= query param.
      if (m.type === "user") {
        const t = localStorage.getItem("psd_token");
        if (t) src += (src.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(t);
      }
      audio.src = src; audio.play().catch(() => toast.error("Müzik önizleme yüklenemedi"));
      setPlaying(m.music_id);
      audio.onended = () => setPlaying(null);
    }
  };

  const queryParsel = async () => {
    if (!form.il || !form.ilce || !form.mahalle) {
      toast.error("İl, ilçe ve mahalle bilgilerini girin");
      return;
    }
    if (!form.ada && !form.parsel) {
      toast.error("Ada veya parsel bilgilerinden en az birini girin");
      return;
    }
    setParselLoading(true);
    try {
      const { data } = await api.post("/parsel/query", {
        il: form.il, ilce: form.ilce, mahalle: form.mahalle,
        ada: form.ada, parsel: form.parsel,
      });
      setParselData(data);
      // Auto-fill zoning info if nitelik available (real land type)
      if (data.nitelik && !form.zoning_info) {
        update("zoning_info", data.nitelik);
      }
      toast.success(`Parsel bulundu: ${data.area_label || data.area_m2 + " m²"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Parsel sorgulanamadı");
      setParselData(null);
    } finally {
      setParselLoading(false);
    }
  };

  const generate = async () => {
    if ((user?.credits ?? 0) < 1) {
      toast.error("Krediniz yetersiz. Lütfen paket satın alın.");
      navigate("/dashboard/packages");
      return;
    }
    const center = parselData?.coordinates ? centroidOf(parselData.coordinates) : null;
    if (!center) {
      toast.error("Uydu görüntüsü için parsel koordinatı gerekli. Lütfen 1. adımda 'Parsel Sorgula' ile parseli bulun.");
      setStep(1);
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        ...form,
        latitude: center.latitude,
        longitude: center.longitude,
        area_label: parselData?.area_label || (parselData?.area_m2 ? String(parselData.area_m2) : null),
      };
      const { data } = await api.post("/videos", payload);
      await refreshUser();
      const videoId = data.video_id;
      const startedAt = Date.now();
      const maxWaitMs = 8 * 60 * 1000; // 8 dakika
      toast.info("Sinematik videonuz hazırlanıyor — uydu görüntüleri, seslendirme ve montaj ~1-3 dakika sürer…");

      const poll = async () => {
        while (Date.now() - startedAt < maxWaitMs) {
          await new Promise(r => setTimeout(r, 6000));
          try {
            const { data: status } = await api.get(`/videos/${videoId}/status`);
            if (status.status === "ready") {
              const fullUrl = status.video_url?.startsWith("http")
                ? status.video_url
                : `${process.env.REACT_APP_BACKEND_URL}${status.video_url}`;
              setResult({ ...status, video_url: fullUrl });
              await refreshUser();
              toast.success("Videonuz hazır!");
              return;
            }
            if (status.status === "failed") {
              toast.error(status.error || "Video üretimi başarısız. Krediniz düşülmedi.");
              await refreshUser();
              setGenerating(false);
              return;
            }
          } catch (e) {
            // continue polling
          }
        }
        toast.error("Zaman aşımı — video paneliniz arka planda hazırlanmaya devam ediyor.");
        navigate("/dashboard/videos");
      };
      await poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video oluşturulamadı");
    } finally {
      setGenerating(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return form.il && form.ilce && form.mahalle && (form.ada || form.parsel);
    if (step === 2) return form.company_name;
    if (step === 3) return form.music_id;
    return true;
  };

  if (result) {
    return (
      <div className="p-6 md:p-12 max-w-5xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-3">Başarılı</div>
        <h1 className="font-display text-4xl font-bold mb-8">Videonuz hazır! 🎬</h1>
        <div className="rounded-xl overflow-hidden border border-[#C29B62]/30 bg-slate-900/40">
          <div className="relative">
            <video src={result.video_url} poster={result.thumbnail_url} controls data-testid="generated-video"
              className="w-full aspect-[9/16] bg-black max-w-[420px] mx-auto block" />
            {form.logo_data && (
              <img src={form.logo_data} alt="watermark" className="absolute top-4 right-4 max-w-[100px] max-h-[60px] opacity-80 pointer-events-none" />
            )}
          </div>
          <div className="p-6 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">Pazarlama Metni</div>
            <p className="text-slate-200 leading-relaxed">{result.narration}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          <a href={result.video_url} download data-testid="download-video-btn"
            className="px-5 py-3 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition">
            Videoyu İndir
          </a>
          {result.share_slug && (
            <button onClick={copyShareLink} data-testid="copy-share-link-btn"
              className="px-5 py-3 rounded-md border border-[#C29B62]/40 text-[#C29B62] hover:bg-[#C29B62]/10 transition inline-flex items-center gap-2">
              <LinkIcon size={16} /> Paylaşım Linki Kopyala
            </button>
          )}
          <button onClick={() => navigate("/dashboard/videos")} data-testid="view-all-videos-btn"
            className="px-5 py-3 rounded-md border border-white/15 hover:bg-white/5 transition">
            Tüm Videolarım
          </button>
          <button onClick={() => { setResult(null); setStep(1); setForm({
            il: "", ilce: "", mahalle: "", ada: "", parsel: "",
            company_name: "", logo_data: null, music_id: "",
            distance_to_road: "", distance_to_sea: "", zoning_info: "", notes: "",
          }); setParselData(null); }}
            className="px-5 py-3 rounded-md border border-white/15 hover:bg-white/5 transition">
            Yeni Video
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-3">Video Oluştur</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Yeni Drone Videosu</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10 overflow-x-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-3 px-4 py-2 rounded-md border transition shrink-0
              ${step === s.id ? "border-[#C29B62] bg-[#C29B62]/10 text-[#C29B62]" :
                step > s.id ? "border-[#C29B62]/40 text-[#C29B62]/70" :
                  "border-white/10 text-slate-500"}`}>
              <s.icon size={16} />
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px w-8 ${step > s.id ? "bg-[#C29B62]/40" : "bg-white/10"}`} />}
          </React.Fragment>
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-slate-900/40 border border-white/10 p-8">
        {step === 1 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl font-semibold mb-4">Konum Bilgileri</h2>
            <p className="text-sm text-slate-400 -mt-2 mb-2">Ada ve parsel'in en az birini girmeniz yeterlidir. Video, parselin gerçek uydu görüntüleriyle üretilir.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="İl" value={form.il} onChange={v => update("il", v)} placeholder="İstanbul" testId="input-il" />
              <Field label="İlçe" value={form.ilce} onChange={v => update("ilce", v)} placeholder="Beykoz" testId="input-ilce" />
              <Field label="Mahalle" value={form.mahalle} onChange={v => update("mahalle", v)} placeholder="Merkez" testId="input-mahalle" />
              <Field label="Ada (opsiyonel)" value={form.ada} onChange={v => update("ada", v)} placeholder="1234" testId="input-ada" />
              <Field label="Parsel (opsiyonel)" value={form.parsel} onChange={v => update("parsel", v)} placeholder="56" testId="input-parsel" />
              <div className="flex items-end">
                <button type="button" onClick={queryParsel} disabled={parselLoading} data-testid="query-parsel-btn"
                  className="w-full py-3 rounded-md border border-[#C29B62]/40 text-[#C29B62] hover:bg-[#C29B62]/10 transition flex items-center justify-center gap-2 disabled:opacity-50">
                  {parselLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  Parsel Sorgula
                </button>
              </div>
            </div>
            {parselData && (
              <div data-testid="parsel-result" className="p-5 rounded-lg bg-[#C29B62]/5 border border-[#C29B62]/30 mt-4">
                <div className="text-xs uppercase tracking-[0.25em] text-[#C29B62] mb-3">
                  {parselData.source} • Sonuç
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <Info2 label="Konum" value={parselData.location} />
                  <Info2 label="Ada / Parsel" value={parselData.ada_parsel} />
                  <Info2 label="Alan" value={parselData.area_label ? `${parselData.area_label} m²` : (parselData.area_m2 ? `${parselData.area_m2} m²` : "-")} />
                  <Info2 label="Mevkii" value={parselData.mevkii || "-"} />
                  <Info2 label="Nitelik" value={parselData.nitelik || "-"} />
                  <Info2 label="Pafta" value={parselData.pafta || "-"} />
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-slate-400">
                  {parselData.has_geometry
                    ? "✓ Parsel koordinatları alındı — video gerçek uydu görüntüleriyle üretilecek."
                    : "Uyarı: Parsel geometrisi bulunamadı, video üretilemez. Farklı bir parsel deneyin."}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h2 className="font-display text-2xl font-semibold mb-4">Firma & Logo</h2>
            <Field label="Firma Adı" value={form.company_name} onChange={v => update("company_name", v)} placeholder="Örnek Emlak" testId="input-company" />
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Logo (PNG/SVG, max 2MB)</label>
              <div {...getRootProps()} data-testid="logo-dropzone"
                className={`p-10 rounded-lg border-2 border-dashed cursor-pointer text-center transition
                  ${isDragActive ? "border-[#C29B62] bg-[#C29B62]/5" : "border-white/15 hover:border-[#C29B62]/50 hover:bg-white/5"}`}>
                <input {...getInputProps()} />
                {form.logo_data ? (
                  <div className="flex items-center justify-center gap-4">
                    <img src={form.logo_data} alt="logo" className="max-h-20 max-w-40 object-contain" />
                    <div className="text-sm text-slate-400">Değiştirmek için tıkla</div>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="mx-auto text-[#C29B62] mb-3" />
                    <p className="text-slate-300 mb-1">Logonuzu sürükleyin veya tıklayın</p>
                    <p className="text-xs text-slate-500">Şeffaf arka plan önerilir</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="font-display text-2xl font-semibold mb-2">Fon Müziği</h2>
            <p className="text-sm text-slate-400 mb-6">
              Katalogdan bir parça seçin veya kendi müziğinizi yükleyin. Göz simgesiyle parçaları katalogda tutup kaldırabilirsiniz.
              Fon müziği seslendirmenin altında kısık seviyede çalar.
            </p>

            {/* Upload dropzone */}
            <div {...musicDrop.getRootProps()} data-testid="music-dropzone"
              className={`p-6 rounded-lg border-2 border-dashed cursor-pointer text-center transition mb-6
                ${musicDrop.isDragActive ? "border-[#C29B62] bg-[#C29B62]/5" : "border-white/15 hover:border-[#C29B62]/50 hover:bg-white/5"}
                ${musicUploading ? "opacity-60 pointer-events-none" : ""}`}>
              <input {...musicDrop.getInputProps()} />
              {musicUploading ? (
                <div className="flex items-center justify-center gap-2 text-[#C29B62]">
                  <Loader2 size={18} className="animate-spin" /> Müziğiniz yükleniyor…
                </div>
              ) : (
                <>
                  <Upload size={24} className="mx-auto text-[#C29B62] mb-2" />
                  <p className="text-slate-300 mb-1 text-sm">Kendi müziğinizi sürükleyin veya tıklayın</p>
                  <p className="text-xs text-slate-500">mp3, wav, m4a, ogg, aac · max 8MB · ilk 60sn kullanılır</p>
                </>
              )}
            </div>

            {music.filter(m => m.type === "user").length > 0 && (
              <>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Yüklediklerim</div>
                <div className="space-y-3 mb-8">
                  {music.filter(m => m.type === "user").map(m => (
                    <MusicRow key={m.music_id} m={m} form={form} update={update}
                      playing={playing} togglePlay={togglePlay}
                      onDelete={() => deleteUserMusic(m)} />
                  ))}
                </div>
              </>
            )}

            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Hazır Katalog</div>
            {GENRE_ORDER.map(genre => {
              const tracks = music.filter(m => m.type !== "user" && m.genre === genre);
              if (!tracks.length) return null;
              return (
                <div key={genre} className="mb-6">
                  <div className="text-sm font-semibold text-[#C29B62] mb-2">{genre}</div>
                  <div className="space-y-3">
                    {tracks.map(m => (
                      <MusicRow key={m.music_id} m={m} form={form} update={update}
                        playing={playing} togglePlay={togglePlay}
                        onToggleCatalog={() => toggleCatalog(m)} />
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-slate-500 mt-4">
              Hazır müzikler: Kevin MacLeod (incompetech.com) — CC BY 4.0 lisansıyla, ticari kullanıma uygundur.
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl font-semibold mb-4">Pazarlama Detayları</h2>
            <p className="text-sm text-slate-400 -mt-3 mb-4">Bu bilgiler Türkçe sesli pazarlama metnine ve ekrandaki yazılara yansır. İmar bilgisi TKGM'den gelmişse otomatik doldurulmuştur.</p>
            <Field label="Ana Yola Mesafe" value={form.distance_to_road} onChange={v => update("distance_to_road", v)} placeholder="120 metre" testId="input-road" />
            <Field label="Denize Mesafe (opsiyonel)" value={form.distance_to_sea} onChange={v => update("distance_to_sea", v)} placeholder="500 metre" testId="input-sea" />
            <Field label="İmar Bilgisi" value={form.zoning_info} onChange={v => update("zoning_info", v)} placeholder="Konut + Ticaret" testId="input-zoning" />
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Ek Not (opsiyonel)</label>
              <textarea data-testid="input-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)}
                rows={3} placeholder="Eklemek istediğiniz özellikler…"
                className="w-full bg-slate-950/60 border border-white/10 rounded-md px-3 py-3 text-slate-100 placeholder:text-slate-600 focus:border-[#C29B62] focus:outline-none focus:ring-1 focus:ring-[#C29B62]" />
            </div>
            <div className="p-4 rounded-lg bg-slate-950/40 border border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Coins size={16} className="text-[#C29B62]" /> Video üretimi 1 kredi harcar. Kredi yalnızca video başarıyla üretilirse düşülür.
              </div>
              <div className="text-sm text-slate-400">Kalan: <span className="text-[#C29B62] font-bold">{user?.credits ?? 0}</span></div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
          <button disabled={step === 1} onClick={() => setStep(s => s - 1)} data-testid="wizard-back-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/15 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition">
            <ArrowLeft size={16} /> Geri
          </button>
          {step < 4 ? (
            <button disabled={!canProceed()} onClick={() => setStep(s => s + 1)} data-testid="wizard-next-btn"
              className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] disabled:opacity-40 disabled:cursor-not-allowed transition">
              İleri <ArrowRight size={16} />
            </button>
          ) : (
            <button disabled={generating} onClick={generate} data-testid="wizard-generate-btn"
              className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] disabled:opacity-50 transition">
              {generating ? <><Loader2 size={16} className="animate-spin" /> Üretiliyor…</> : <>Videoyu Oluştur <ArrowRight size={16} /></>}
            </button>
          )}
        </div>
      </motion.div>

      {generating && (
        <div data-testid="generating-overlay" className="mt-6 p-6 rounded-xl bg-[#C29B62]/5 border border-[#C29B62]/30 flex items-center gap-4">
          <Loader2 size={28} className="text-[#C29B62] animate-spin shrink-0" />
          <div>
            <div className="font-display text-lg font-semibold text-[#C29B62]">Sinematik videonuz üretiliyor</div>
            <div className="text-sm text-slate-300 mt-1">Parselin uydu görüntüleri alınıyor, Türkçe seslendirme hazırlanıyor ve 60 saniyelik montaj yapılıyor. Genellikle 1–3 dakika sürer; sayfayı kapatmayın.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-slate-950/60 border border-white/10 rounded-md px-3 py-3 text-slate-100 placeholder:text-slate-600 focus:border-[#C29B62] focus:outline-none focus:ring-1 focus:ring-[#C29B62]" />
    </div>
  );
}

function Info2({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-slate-200 font-semibold mt-1">{value}</div>
    </div>
  );
}


function MusicRow({ m, form, update, playing, togglePlay, onDelete, onToggleCatalog }) {
  const selected = form.music_id === m.music_id;
  const enabled = m.enabled !== false;
  return (
    <div data-testid={`music-${m.music_id}`}
      className={`flex items-center gap-4 p-4 rounded-lg border transition
        ${!enabled ? "opacity-45 border-white/5" : "cursor-pointer"}
        ${selected ? "border-[#C29B62] bg-[#C29B62]/5" : enabled ? "border-white/10 hover:border-white/20" : ""}`}
      onClick={() => { if (enabled) update("music_id", m.music_id); }}>
      <button type="button" data-testid={`music-play-${m.music_id}`}
        onClick={(e) => { e.stopPropagation(); togglePlay(m); }}
        className="w-10 h-10 rounded-full bg-[#C29B62] text-slate-950 flex items-center justify-center hover:bg-[#D4AF7A] shrink-0">
        {playing === m.music_id ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{m.name}</div>
        <div className="text-xs text-slate-400 mt-1">{m.mood}{m.artist ? ` · ${m.artist}` : ""}</div>
      </div>
      {selected && <Check size={18} className="text-[#C29B62] shrink-0" />}
      {onToggleCatalog && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggleCatalog(); }}
          data-testid={`music-catalog-toggle-${m.music_id}`}
          title={enabled ? "Katalogdan kaldır" : "Kataloğa ekle"}
          className={`p-2 rounded-md shrink-0 transition ${enabled ? "text-slate-500 hover:text-[#C29B62] hover:bg-[#C29B62]/10" : "text-[#C29B62] hover:bg-[#C29B62]/10"}`}>
          {enabled ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      )}
      {onDelete && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`music-delete-${m.music_id}`}
          className="p-2 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 shrink-0">
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}
